import React, { useState } from 'react';
import { PoDetail, PoInvoice, PoDelivery, PoJobcard, PoOther, PoQuotation, PoTicket } from './poSlice';
import { formatDate, formatCurrency } from '../../utils/formatUtils';

interface ViewDocumentsModalProps {
  po: PoDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onDeleteDocument?: (type: string, documentId: number) => Promise<void>;
}

const ViewDocumentsModal: React.FC<ViewDocumentsModalProps> = ({ po, isOpen, onClose, onDeleteDocument }) => {
  const [activeTab, setActiveTab] = useState<string>('');

  // Set first available tab as active when modal opens
  React.useEffect(() => {
    if (isOpen && po) {
      if (po.quotation) setActiveTab('quotation');
      else if (po.invoices && po.invoices.length > 0) setActiveTab('invoice');
      else if (po.deliverynotes && po.deliverynotes.length > 0) setActiveTab('delivery');
      else if (po.jobcards && po.jobcards.length > 0) setActiveTab('jobcard');
      else if (po.tickets && po.tickets.length > 0) setActiveTab('ticket');
      else if (po.others && po.others.length > 0) setActiveTab('other');
    }
  }, [isOpen, po]);

  if (!isOpen || !po) return null;

  const hasQuotation = !!po?.quotation;
  const hasInvoices = po?.invoices && po.invoices.length > 0;
  const hasDeliveryNotes = po?.deliverynotes && po.deliverynotes.length > 0;
  const hasJobcards = po?.jobcards && po.jobcards.length > 0;
  const hasTickets = po?.tickets && po.tickets.length > 0;
  const hasOthers = po?.others && po.others.length > 0;

  const hasAnyDocuments = hasQuotation || hasInvoices || hasDeliveryNotes || hasJobcards || hasTickets || hasOthers;

  const getAssetUrl = (path: string, folder: string) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}/assets/${folder}/${path}`;
  };

  const getFileExtension = (filename: string) => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const isPdf = (filename: string) => {
    return getFileExtension(filename) === 'pdf';
  };

  const renderDocumentViewer = (attachment: string, folder: string, title?: string) => {
    const url = getAssetUrl(attachment, folder);
    
    if (folder === 'Other' || !isPdf(attachment)) {
      // For non-PDF files or "Other" type, show download link
      return (
        <div className="flex flex-col items-center justify-center p-4 bg-gray-100 dark:bg-gray-800 rounded">
          <div className="text-center">
            <svg className="w-10 h-10 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{title || attachment}</p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download / Open
            </a>
          </div>
        </div>
      );
    }

    // For PDF files, show iframe
    return (
      <iframe
        src={url}
        className="w-full h-full border-0"
        title="Document Preview"
      />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h5 className="text-lg font-semibold text-gray-900 dark:text-gray-100">View Documents</h5>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          {!hasAnyDocuments ? (
            <div className="flex items-center justify-center h-full p-8">
              <p className="text-gray-500 dark:text-gray-400">No documents attached to this purchase order.</p>
            </div>
          ) : (
            <>
              {/* Tab Navigation */}
              <div className="flex border-b dark:border-gray-700 overflow-x-auto flex-shrink-0">
                {hasQuotation && (
                  <button
                    onClick={() => setActiveTab('quotation')}
                    className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'quotation'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    Quotation (1)
                  </button>
                )}
                {hasInvoices && (
                  <button
                    onClick={() => setActiveTab('invoice')}
                    className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'invoice'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    Invoice ({po.invoices?.length || 0})
                  </button>
                )}
                {hasDeliveryNotes && (
                  <button
                    onClick={() => setActiveTab('delivery')}
                    className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'delivery'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    Delivery Note ({po.deliverynotes?.length || 0})
                  </button>
                )}
                {hasJobcards && (
                  <button
                    onClick={() => setActiveTab('jobcard')}
                    className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'jobcard'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    JobCard ({po.jobcards?.length || 0})
                  </button>
                )}
                {hasTickets && (
                  <button
                    onClick={() => setActiveTab('ticket')}
                    className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'ticket'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    Colefax Ticket ({po.tickets?.length || 0})
                  </button>
                )}
                {hasOthers && (
                  <button
                    onClick={() => setActiveTab('other')}
                    className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'other'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-b-2 border-blue-600'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    Other ({po.others?.length || 0})
                  </button>
                )}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800 min-h-0">
                {activeTab === 'quotation' && hasQuotation && (
                  <div className="p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Quotation Details</h3>
                        {onDeleteDocument && po.quotation?.id && !(!!po.order_number && po.status_id === 2) && (
                          <button
                            onClick={() => onDeleteDocument('quotation', po.quotation!.id)}
                            className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {po?.quotation?.quotation_number && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Quotation Number:</span>
                            <span className="ml-2 text-gray-900 dark:text-gray-100">{po.quotation.quotation_number}</span>
                          </div>
                        )}
                        {po?.quotation?.date && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Date:</span>
                            <span className="ml-2 text-gray-900 dark:text-gray-100">{formatDate(po.quotation.date)}</span>
                          </div>
                        )}
                        {po?.quotation?.amount && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Amount:</span>
                            <span className="ml-2 text-gray-900 dark:text-gray-100">{formatCurrency(po.quotation.amount)}</span>
                          </div>
                        )}
                        {po?.quotation?.vat !== undefined && po?.quotation?.vat !== null && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">VAT:</span>
                            <span className="ml-2 text-gray-900 dark:text-gray-100">{po.quotation.vat ? 'Included' : 'Excluded'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="h-[400px]">
                      {po?.quotation?.attachment ? (
                        renderDocumentViewer(po.quotation.attachment, 'Quotes')
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                          No quotation file attached.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'invoice' && hasInvoices && (
                  <div className="space-y-6 p-4">
                    {po?.invoices && po.invoices.length > 0 && (
                      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            Total Invoice Amount ({po.invoices.length} invoice{po.invoices.length !== 1 ? 's' : ''}):
                          </span>
                          <span className="text-lg font-bold text-blue-700 dark:text-blue-300">
                            {formatCurrency(po.invoices.reduce((sum: number, inv) => sum + (typeof inv.amount === 'string' ? parseFloat(inv.amount) : (inv.amount || 0)), 0))}
                          </span>
                        </div>
                      </div>
                    )}
                    {po?.invoices?.map((invoice: PoInvoice, index: number) => (
                      <div key={invoice.id || index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="mb-4">
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              Invoice Details
                              <span className="ml-2 px-2 py-0.5 text-xs font-normal rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">#{index + 1}</span>
                            </h3>
                            {onDeleteDocument && invoice.id && (
                              <button
                                onClick={() => onDeleteDocument('invoice', invoice.id)}
                                className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {invoice.invoice_number && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Invoice Number:</span>
                                <span className="ml-2 text-gray-900 dark:text-gray-100">{invoice.invoice_number}</span>
                              </div>
                            )}
                            {invoice.date && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Date:</span>
                                <span className="ml-2 text-gray-900 dark:text-gray-100">{formatDate(invoice.date)}</span>
                              </div>
                            )}
                            {invoice.amount && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Amount:</span>
                                <span className="ml-2 text-gray-900 dark:text-gray-100">{formatCurrency(typeof invoice.amount === 'string' ? parseFloat(invoice.amount) : invoice.amount)}</span>
                              </div>
                            )}
                            {invoice.vat !== undefined && invoice.vat !== null && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">VAT:</span>
                                <span className="ml-2 text-gray-900 dark:text-gray-100">{invoice.vat ? 'Included' : 'Excluded'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="h-[400px]">
                          {invoice.attachment ? (
                            renderDocumentViewer(invoice.attachment, 'Invoices', invoice.invoice_number)
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                              No invoice file attached.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'delivery' && hasDeliveryNotes && (
                  <div className="space-y-6 p-4">
                    {po?.deliverynotes?.map((delivery: PoDelivery, index: number) => (
                      <div key={delivery.id || index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="mb-4">
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              Delivery Note Details
                              <span className="ml-2 px-2 py-0.5 text-xs font-normal rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">#{index + 1}</span>
                            </h3>
                            {onDeleteDocument && delivery.id && (
                              <button
                                onClick={() => onDeleteDocument('delivery', delivery.id)}
                                className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {delivery.delivery_number && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Delivery Number:</span>
                                <span className="ml-2 text-gray-900 dark:text-gray-100">{delivery.delivery_number}</span>
                              </div>
                            )}
                            {delivery.date && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Date:</span>
                                <span className="ml-2 text-gray-900 dark:text-gray-100">{formatDate(delivery.date)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="h-[400px]">
                          {delivery.attachment ? (
                            renderDocumentViewer(delivery.attachment, 'DeliveryNotes', delivery.delivery_number)
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                              No delivery note file attached.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'jobcard' && hasJobcards && (
                  <div className="space-y-6 p-4">
                    {po?.jobcards?.map((jobcard: PoJobcard, index: number) => (
                      <div key={jobcard.id || index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="mb-4">
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              Jobcard Details
                              <span className="ml-2 px-2 py-0.5 text-xs font-normal rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">#{index + 1}</span>
                            </h3>
                            {onDeleteDocument && jobcard.id && (
                              <button
                                onClick={() => onDeleteDocument('jobcard', jobcard.id)}
                                className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {jobcard.job_number && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Job Number:</span>
                                <span className="ml-2 text-gray-900 dark:text-gray-100">{jobcard.job_number}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="h-[400px]">
                          {jobcard.attachment ? (
                            renderDocumentViewer(jobcard.attachment, 'Jobcards', jobcard.job_number)
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                              No jobcard file attached.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'ticket' && hasTickets && (
                  <div className="space-y-6 p-4">
                    {po?.tickets?.map((ticket: PoTicket, index: number) => (
                      <div key={ticket.id || index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="mb-4">
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              Colefax Ticket Details
                              <span className="ml-2 px-2 py-0.5 text-xs font-normal rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">#{index + 1}</span>
                            </h3>
                            {onDeleteDocument && ticket.id && (
                              <button
                                onClick={() => onDeleteDocument('ticket', ticket.id)}
                                className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-4 text-sm">
                            {ticket.comment && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Comment:</span>
                                <p className="ml-2 mt-1 text-gray-900 dark:text-gray-100">{ticket.comment}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="h-[400px]">
                          {ticket.attachment ? (
                            renderDocumentViewer(ticket.attachment, 'Tickets')
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                              No ticket file attached.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'other' && hasOthers && (
                  <div className="space-y-6 p-4">
                    {po?.others?.map((other: PoOther, index: number) => (
                      <div key={other.id || index} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="mb-4">
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                              Other Document Details
                              <span className="ml-2 px-2 py-0.5 text-xs font-normal rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">#{index + 1}</span>
                            </h3>
                            {onDeleteDocument && other.id && (
                              <button
                                onClick={() => onDeleteDocument('other', other.id)}
                                className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-4 text-sm">
                            {other.title && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Title:</span>
                                <span className="ml-2 text-gray-900 dark:text-gray-100">{other.title}</span>
                              </div>
                            )}
                            {other.description && (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Description:</span>
                                <p className="ml-2 mt-1 text-gray-900 dark:text-gray-100">{other.description}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          {other.attachment ? (
                            renderDocumentViewer(other.attachment, 'Other', other.title)
                          ) : (
                            <div className="flex items-center justify-center h-20 text-gray-500 dark:text-gray-400">
                              No file attached.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-center p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewDocumentsModal;
